import { Coffee, BookOpen, Briefcase, Video, Users, Timer, BookCheck, UsersRound, CheckSquare, Bell } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const modes = [
  {
    id: "chill",
    title: "Chill",
    description: "Relax and connect with others in a comfortable environment",
    icon: Coffee,
    color: "hsl(328 85% 55%)",
    features: [
      { icon: Video, label: "Stream" },
      { icon: Users, label: "Friends" },
    ],
  },
  {
    id: "study",
    title: "Study",
    description: "Focus on learning with structured tools and accountability",
    icon: BookOpen,
    color: "hsl(193 95% 50%)",
    features: [
      { icon: Timer, label: "Pomodoro" },
      { icon: BookCheck, label: "Quizzes" },
      { icon: UsersRound, label: "Groups" },
    ],
  },
  {
    id: "work",
    title: "Work",
    description: "Stay productive with task management and reminders",
    icon: Briefcase,
    color: "hsl(38 92% 50%)",
    features: [
      { icon: CheckSquare, label: "Tasks" },
      { icon: Bell, label: "Reminders" },
    ],
  },
];

export const Modes = () => {
  return (
    <section className="py-20 px-4 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background" />
      
      <div className="container mx-auto relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
            Choose Your Mode
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Whether you're relaxing, studying, or working, CSW adapts to your needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {modes.map((mode, index) => {
            const Icon = mode.icon;
            return (
              <Card
                key={mode.id}
                className="group relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 hover:scale-105 hover:shadow-xl animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Gradient overlay */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                  style={{ background: `linear-gradient(135deg, ${mode.color} 0%, transparent 100%)` }}
                />
                
                <CardHeader className="text-center pb-4">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${mode.color}15` }}
                  >
                    <Icon 
                      className="w-8 h-8 transition-colors duration-300" 
                      style={{ color: mode.color }}
                    />
                  </div>
                  <CardTitle className="text-2xl font-bold" style={{ color: mode.color }}>
                    {mode.title}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {mode.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-3">
                    {mode.features.map((feature) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div 
                          key={feature.label}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent/50 transition-colors duration-200"
                        >
                          <FeatureIcon className="w-5 h-5 text-muted-foreground" />
                          <span className="text-foreground font-medium">{feature.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
